import { Bus, GraduationCap, Info, MapPin, Plus, Search, TrainFront, X, Zap } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { tx } from '../../i18n';
import { num } from '../../lib/values';
import type { AreaPlacesResponse } from '../../types/api';

/** What the screen is searching for. Every field is carried in the URL, so a search is a link. */
export interface Criteria {
  outcodes: string[];
  /** A band letter, or null for any. */
  minBand: string | null;
  phase: string | null;
  schoolMetres: number | null;
  goodOnly: boolean;
  stationMetres: number | null;
  busMetres: number | null;
}

interface AreaCriteriaProps {
  value: Criteria;
  places: AreaPlacesResponse | null;
  onChange: (next: Criteria) => void;
  /** Runs the search. Until it is pressed the form is a draft and the result below is the old one. */
  onSearch: () => void;
  /** True while the form says something the result on screen was not searched with. */
  dirty: boolean;
  loading: boolean;
}

/** What choosing a named place brought with it, said where the choice was made. */
interface PlaceNote {
  name: string;
  convention: boolean;
  missing: string[];
}

const BANDS = ['A', 'B', 'C', 'D', 'E', 'F', 'G'] as const;
const WALKS = [200, 400, 800, 1200, 2000] as const;

/** Beyond this many unread outward codes, name a few and count the rest. */
const NAME_AT_MOST = 6;

/** What an outward code looks like, so typed text can be told apart from a place name. */
const OUTCODE = /^[A-Z]{1,2}\d[A-Z\d]?$/;

/** Where each band starts, so a reader who thinks in numbers can be answered in numbers. */
const BAND_FLOOR: Record<string, number> = { A: 92, B: 81, C: 69, D: 55, E: 39, F: 21, G: 1 };

/**
 * The offered walks, plus the one in force if it is not among them.
 *
 * <p>A sentence can say "600 m", and the search will honour it; a select with no matching option
 * draws blank, which would show a criterion as absent while it is filtering the result. So an
 * unlisted distance joins the list in its right place rather than disappearing from the form.
 */
function steps(current: number | null): number[] {
  const all = current !== null && !WALKS.includes(current as (typeof WALKS)[number])
    ? [...WALKS, current]
    : [...WALKS];
  return all.sort((a, b) => a - b);
}

/**
 * The criteria, as controls.
 *
 * <p>A form with a Search button rather than a search that runs as you type: six criteria are
 * usually changed two or three at a time, and a result that redraws after each one is a result
 * that is wrong more often than it is right. Nothing is searched until the button is pressed, and
 * while the form says something the result below was not searched with, it says so.
 *
 * <p>The area picker offers what has actually been crawled, and says how much of each named place
 * that covers. Choosing from what exists is the difference between "north london: 2 of 50 read"
 * before searching and an apology afterwards.
 */
export default function AreaCriteria({ value, places, onChange, onSearch, dirty, loading }: AreaCriteriaProps) {
  const [picker, setPicker] = useState('');
  const container = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState<PlaceNote | null>(null);
  const [moveToList, setMoveToList] = useState(false);

  useEffect(() => {
    if (!moveToList) {
      return;
    }
    container.current?.querySelector<HTMLButtonElement>('.criteria__options button')?.focus();
    setMoveToList(false);
  }, [moveToList]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const close = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const set = (patch: Partial<Criteria>) => onChange({ ...value, ...patch });

  const ready = new Set((places?.crawled ?? []).map((row) => row.outcode));
  const search = picker.trim().toUpperCase();

  const outcodeMatches = (places?.crawled ?? [])
    .filter((row) => !value.outcodes.includes(row.outcode))
    .filter((row) => search === '' || row.outcode.startsWith(search))
    .slice(0, 8);

  const nameMatches = (places?.names ?? [])
    .filter((place) => place.ready > 0)
    .filter((place) => search === '' || place.name.toUpperCase().includes(search))
    .slice(0, 6);

  /**
   * Adds outward codes to the criteria.
   *
   * <p>A code that has not been read yet is added like any other: the search asks for it to be
   * read and shows that happening. Only the picker's named places are narrowed to what exists,
   * because "north london" resolving to fifty unread outcodes would start fifty readings.
   */
  const addOutcodes = (codes: string[], from?: PlaceNote) => {
    const next = [...value.outcodes];
    codes.forEach((code) => {
      if (!next.includes(code)) {
        next.push(code);
      }
    });
    set({ outcodes: next });
    setNote(from ?? null);
    setPicker('');
    setOpen(false);
  };

  const removeOutcode = (outcode: string) => {
    setNote(null);
    set({ outcodes: value.outcodes.filter((code) => code !== outcode) });
  };

  const nothingMatches = search !== '' && outcodeMatches.length === 0 && nameMatches.length === 0;
  const typedOutcode = OUTCODE.test(search);
  const nothingRead = places !== null && places.crawled.length === 0;

  return (
    <form
      className="criteria"
      onSubmit={(event) => {
        event.preventDefault();
        onSearch();
      }}
    >
      {/* ---------------------------------------------------------- area */}
      <div className="criteria__field criteria__field--area" ref={container}>
        <span className="field-label" id="criteria-area-label">
          <MapPin size={14} aria-hidden="true" /> {tx('Area', 'Bölge', 'Obszar', 'Área', 'Zona')}
        </span>

        {value.outcodes.length > 0 ? (
          <ul className="chips">
            {value.outcodes.map((outcode) => (
              <li key={outcode}>
                <button
                  type="button"
                  className="chip-button"
                  onClick={() => removeOutcode(outcode)}
                >
                  {outcode}
                  <X size={13} aria-hidden="true" />
                  <span className="sr-only">
                    {tx(`Remove ${outcode}`, `${outcode} bölgesini çıkar`, `Usuń ${outcode}`, `Remover ${outcode}`, `Quitar ${outcode}`)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : null}

        <input
          type="text"
          className="input"
          value={picker}
          aria-labelledby="criteria-area-label"
          placeholder={tx(
            'Outward code or place',
            'Outcode ya da yer adı',
            'Kod lub miejsce',
            'Código ou lugar',
            'Código o lugar',
          )}
          onFocus={() => setOpen(true)}
          onChange={(event) => {
            setPicker(event.target.value);
            setOpen(true);
          }}
          /*
           * Enter here adds the first match rather than submitting the form. Somebody halfway
           * through typing a place name has not finished choosing an area, and searching on that
           * keystroke would run a search they did not ask for - and lose what they had typed.
           */
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              const first = nameMatches[0];
              if (first) {
                addOutcodes(first.outcodes.filter((code) => ready.has(code)), {
                  name: first.name,
                  convention: first.convention,
                  missing: first.outcodes.filter((code) => !ready.has(code)),
                });
              } else if (outcodeMatches[0]) {
                addOutcodes([outcodeMatches[0].outcode]);
              } else if (OUTCODE.test(search)) {
                addOutcodes([search]);
              }
            } else if (event.key === 'Escape') {
              setOpen(false);
            } else if (event.key === 'ArrowDown') {
              event.preventDefault();
              setOpen(true);
              // The list may not be rendered yet, so the move waits for the render that opens it.
              setMoveToList(true);
            }
          }}
        />

        {open && (outcodeMatches.length > 0 || nameMatches.length > 0) ? (
          <ul className="criteria__options">
            {nameMatches.map((place) => (
              <li key={`name-${place.name}`}>
                <button
                  type="button"
                  onClick={() =>
                    addOutcodes(place.outcodes.filter((code) => ready.has(code)), {
                      name: place.name,
                      convention: place.convention,
                      missing: place.outcodes.filter((code) => !ready.has(code)),
                    })
                  }
                >
                  <span className="criteria__option-name">{place.name}</span>
                  <span className="small muted">
                    {tx(
                      `${place.ready} of ${place.outcodes.length} outward codes read`,
                      `${place.outcodes.length} outcode'dan ${place.ready} tanesi okundu`,
                      `odczytano ${place.ready} z ${place.outcodes.length} kodów`,
                      `${place.ready} de ${place.outcodes.length} códigos lidos`,
                      `${place.ready} de ${place.outcodes.length} códigos leídos`,
                    )}
                    {place.convention
                      ? ` · ${tx('postal areas', 'posta alanları', 'obszary pocztowe', 'áreas postais', 'áreas postales')}`
                      : ''}
                  </span>
                </button>
              </li>
            ))}
            {outcodeMatches.map((row) => (
              <li key={`out-${row.outcode}`}>
                <button type="button" onClick={() => addOutcodes([row.outcode])}>
                  <span className="criteria__option-name">{row.outcode}</span>
                  <span className="small muted">
                    {tx(
                      `${row.postcodesCrawled} of ${row.postcodesKnown} postcodes read`,
                      `${row.postcodesKnown} posta kodundan ${row.postcodesCrawled} tanesi okundu`,
                      `odczytano ${row.postcodesCrawled} z ${row.postcodesKnown} kodów`,
                      `${row.postcodesCrawled} de ${row.postcodesKnown} códigos lidos`,
                      `${row.postcodesCrawled} de ${row.postcodesKnown} códigos leídos`,
                    )}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : null}

        {/*
          * An outward code nobody has read is still a place somebody can ask about: the search
          * asks for it to be read and shows that happening, so the only thing that has to be
          * true here is that what was typed is an outward code. What used to be a dead end - a
          * disabled button over a box with N10 in it - is an offer.
          */}
        {nothingMatches || (nothingRead && search === '') ? (
          <div className="criteria__empty small">
            {typedOutcode ? (
              <>
                <button type="button" className="button button--secondary" onClick={() => addOutcodes([search])}>
                  <Plus size={15} aria-hidden="true" />
                  {tx(`Add ${search}`, `${search} ekle`, `Dodaj ${search}`, `Juntar ${search}`, `Añadir ${search}`)}
                </button>
                <p className="muted">
                  {tx(
                    `${search} has not been read yet. Searching it reads it, which takes a few minutes the first time and nothing after that.`,
                    `${search} henüz okunmadı. Arayınca okunur; ilk seferinde birkaç dakika sürer, sonrasında beklemezsiniz.`,
                    `${search} nie został jeszcze odczytany. Wyszukanie go odczytuje - za pierwszym razem kilka minut, potem od razu.`,
                    `${search} ainda não foi lido. Pesquisá-lo lê-o: alguns minutos da primeira vez e nada depois.`,
                    `${search} todavía no se ha leído. Buscarlo lo lee: unos minutos la primera vez y nada después.`,
                  )}
                </p>
              </>
            ) : (
              <p>
                {search === ''
                  ? tx(
                      'No area has been read yet. Type an outward code, such as N10, and the search will read it.',
                      'Henüz hiçbir bölge okunmadı. Bir outcode yazın, örneğin N10; arama onu okuyacaktır.',
                      'Żaden obszar nie został jeszcze odczytany. Wpisz kod, np. N10, a wyszukiwanie go odczyta.',
                      'Ainda não foi lida nenhuma área. Escreva um código, por exemplo N10, e a pesquisa lê-o.',
                      'Todavía no se ha leído ninguna zona. Escribe un código, por ejemplo N10, y la búsqueda lo leerá.',
                    )
                  : tx(
                      `No area matches "${picker.trim()}". Try an outward code, such as N10.`,
                      `"${picker.trim()}" ile eşleşen bölge yok. Bir outcode deneyin, örneğin N10.`,
                      `Żaden obszar nie pasuje do „${picker.trim()}". Spróbuj kodu, np. N10.`,
                      `Nenhuma zona corresponde a "${picker.trim()}". Experimente um código, por exemplo N10.`,
                      `Ninguna zona coincide con "${picker.trim()}". Prueba un código, por ejemplo N10.`,
                    )}
              </p>
            )}
          </div>
        ) : null}
      </div>

      {/*
        * What a named place brought with it, said next to the field where it was chosen rather
        * than beside the result: a place with no official boundary, and the outward codes of it
        * that nobody has read. Only the crawled ones are added, so without this the gap would be
        * silent - and a reader would believe they had seen north London having seen three
        * districts of it.
        */}
      {note && (note.convention || note.missing.length > 0) ? (
        <p className="criteria__note criteria__field--wide small">
          <Info size={15} aria-hidden="true" />
          <span>
            {note.convention
              ? tx(
                  `"${note.name}" has no official boundary; the closest postcode areas were used.`,
                  `"${note.name}" için resmî bir sınır yok; en yakın posta kodu alanları kullanıldı.`,
                  `„${note.name}" nie ma oficjalnej granicy; użyto najbliższych obszarów pocztowych.`,
                  `"${note.name}" não tem fronteira oficial; usaram-se as áreas postais mais próximas.`,
                  `"${note.name}" no tiene límite oficial; se han usado las áreas postales más cercanas.`,
                )
              : ''}
            {note.missing.length > 0
              ? ` ${tx(
                  `${note.missing.length} of its outward codes have not been read and are not in the search: ${note.missing.slice(0, NAME_AT_MOST).join(', ')}${note.missing.length > NAME_AT_MOST ? '…' : ''}`,
                  `${note.missing.length} outcode'u taranmadı ve aramaya dahil değil: ${note.missing.slice(0, NAME_AT_MOST).join(', ')}${note.missing.length > NAME_AT_MOST ? '…' : ''}`,
                  `${note.missing.length} jego kodów nie zostało odczytanych i nie ma ich w wyszukiwaniu: ${note.missing.slice(0, NAME_AT_MOST).join(', ')}${note.missing.length > NAME_AT_MOST ? '…' : ''}`,
                  `${note.missing.length} dos seus códigos não foram lidos e não entram na pesquisa: ${note.missing.slice(0, NAME_AT_MOST).join(', ')}${note.missing.length > NAME_AT_MOST ? '…' : ''}`,
                  `${note.missing.length} de sus códigos no se han leído y no entran en la búsqueda: ${note.missing.slice(0, NAME_AT_MOST).join(', ')}${note.missing.length > NAME_AT_MOST ? '…' : ''}`,
                )}`
              : ''}
          </span>
        </p>
      ) : null}

      {/* ----------------------------------------------------------- epc */}
      <div className="criteria__field">
        <label className="field-label" htmlFor="criteria-band">
          <Zap size={14} aria-hidden="true" /> {tx('EPC, at least', 'EPC en az', 'EPC co najmniej', 'EPC no mínimo', 'EPC como mínimo')}
        </label>
        <select
          id="criteria-band"
          className="input"
          value={value.minBand ?? ''}
          onChange={(event) => set({ minBand: event.target.value || null })}
        >
          <option value="">{tx('Any band', 'Fark etmez', 'Dowolna', 'Qualquer', 'Cualquiera')}</option>
          {BANDS.map((band) => (
            <option key={band} value={band}>
              {tx(
                `${band} or better`,
                `${band} ve üzeri`,
                `${band} lub lepsza`,
                `${band} ou melhor`,
                `${band} o mejor`,
              )}
            </option>
          ))}
        </select>
        {/*
          * The band's floor, under the control rather than inside it: an option long enough to
          * carry both is an option that truncates in a narrow column, and a truncated criterion
          * is worse than a second line. A reader who asked for "min 70" needs to see which band
          * that became, or they cannot tell whether the search understood them.
          */}
        {value.minBand && BAND_FLOOR[value.minBand] !== undefined ? (
          <p className="criteria__hint small muted">
            {tx(
              `Band ${value.minBand} starts at SAP ${BAND_FLOOR[value.minBand]}.`,
              `${value.minBand} bandı SAP ${BAND_FLOOR[value.minBand]} ile başlar.`,
              `Klasa ${value.minBand} zaczyna się od SAP ${BAND_FLOOR[value.minBand]}.`,
              `A classe ${value.minBand} começa em SAP ${BAND_FLOOR[value.minBand]}.`,
              `La letra ${value.minBand} empieza en SAP ${BAND_FLOOR[value.minBand]}.`,
            )}
          </p>
        ) : null}
      </div>

      {/* -------------------------------------------------------- school */}
      <div className="criteria__field">
        <label className="field-label" htmlFor="criteria-phase">
          <GraduationCap size={14} aria-hidden="true" /> {tx('School type', 'Okul tipi', 'Typ szkoły', 'Tipo de escola', 'Tipo de colegio')}
        </label>
        <select
          id="criteria-phase"
          className="input"
          value={value.phase ?? ''}
          onChange={(event) => {
            const phase = event.target.value || null;
            set({ phase, schoolMetres: phase ? value.schoolMetres ?? 800 : null, goodOnly: phase ? value.goodOnly : false });
          }}
        >
          <option value="">{tx('Not a criterion', 'Kriter değil', 'Nie jest kryterium', 'Não é critério', 'No es criterio')}</option>
          <option value="PRIMARY">{tx('Primary', 'İlkokul', 'Podstawowa', 'Primária', 'Primaria')}</option>
          <option value="SECONDARY">{tx('Secondary', 'Ortaokul / lise', 'Ponadpodstawowa', 'Secundária', 'Secundaria')}</option>
        </select>
      </div>

      <div className="criteria__field">
        <label className="field-label" htmlFor="criteria-school-metres">
          {tx('School, at most', 'Okula en fazla', 'Do szkoły najwyżej', 'À escola, no máximo', 'Al colegio, máximo')}
        </label>
        <select
          id="criteria-school-metres"
          className="input"
          value={value.schoolMetres === null ? '' : String(value.schoolMetres)}
          disabled={!value.phase}
          onChange={(event) => set({ schoolMetres: num(Number(event.target.value)) })}
        >
          {steps(value.schoolMetres).map((metres) => (
            <option key={metres} value={metres}>
              {metres} m
            </option>
          ))}
        </select>
      </div>

      {/* ----------------------------------------------------- transport */}
      <div className="criteria__field">
        <label className="field-label" htmlFor="criteria-station">
          <TrainFront size={14} aria-hidden="true" />{' '}
          {tx('Station, at most', 'Metroya en fazla', 'Do stacji najwyżej', 'À estação, no máximo', 'A la estación, máximo')}
        </label>
        <select
          id="criteria-station"
          className="input"
          value={value.stationMetres === null ? '' : String(value.stationMetres)}
          onChange={(event) => set({ stationMetres: event.target.value ? Number(event.target.value) : null })}
        >
          <option value="">{tx('Not a criterion', 'Kriter değil', 'Nie jest kryterium', 'Não é critério', 'No es criterio')}</option>
          {steps(value.stationMetres).map((metres) => (
            <option key={metres} value={metres}>
              {metres} m
            </option>
          ))}
        </select>
      </div>

      <div className="criteria__field">
        <label className="field-label" htmlFor="criteria-bus">
          <Bus size={14} aria-hidden="true" />{' '}
          {tx('Bus stop, at most', 'Durağa en fazla', 'Do przystanku najwyżej', 'À paragem, no máximo', 'A la parada, máximo')}
        </label>
        <select
          id="criteria-bus"
          className="input"
          value={value.busMetres === null ? '' : String(value.busMetres)}
          onChange={(event) => set({ busMetres: event.target.value ? Number(event.target.value) : null })}
        >
          <option value="">{tx('Not a criterion', 'Kriter değil', 'Nie jest kryterium', 'Não é critério', 'No es criterio')}</option>
          {steps(value.busMetres).map((metres) => (
            <option key={metres} value={metres}>
              {metres} m
            </option>
          ))}
        </select>
      </div>

      <div className="criteria__field criteria__field--check">
        <label className="checkbox">
          <input
            type="checkbox"
            checked={value.goodOnly}
            disabled={!value.phase}
            onChange={(event) => set({ goodOnly: event.target.checked })}
          />
          {tx(
            'Only schools Ofsted graded Good or Outstanding',
            'Yalnızca Ofsted’in Good veya Outstanding verdiği okullar',
            'Tylko szkoły ocenione na Good lub Outstanding',
            'Apenas escolas com Good ou Outstanding',
            'Solo colegios con Good u Outstanding',
          )}
        </label>
      </div>

      {/*
        * The search runs here and nowhere else. The hint beside the button carries the state a
        * disabled control would otherwise leave unexplained, and - once there is a result on
        * screen - says when the form has moved away from it.
        */}
      <div className="criteria__actions">
        <p className="small muted criteria__state" aria-live="polite">
          {value.outcodes.length === 0
            ? tx(
                'Choose an area first.',
                'Önce bir bölge seçin.',
                'Najpierw wybierz obszar.',
                'Escolha primeiro uma área.',
                'Elige primero una zona.',
              )
            : dirty
              ? tx(
                  'The criteria have changed — the result below is the previous search.',
                  'Kriterler değişti — aşağıdaki sonuç bir önceki aramaya ait.',
                  'Kryteria się zmieniły — wynik poniżej pochodzi z poprzedniego wyszukiwania.',
                  'Os critérios mudaram — o resultado abaixo é da pesquisa anterior.',
                  'Los criterios han cambiado: el resultado de abajo es de la búsqueda anterior.',
                )
              : ''}
        </p>
        <button type="submit" className="button" disabled={value.outcodes.length === 0 || loading}>
          <Search size={16} aria-hidden="true" />
          {loading
            ? tx('Searching', 'Aranıyor', 'Szukanie', 'A procurar', 'Buscando')
            : tx('Search', 'Ara', 'Szukaj', 'Procurar', 'Buscar')}
        </button>
      </div>
    </form>
  );
}

/** The criteria as a query string, which is also how the screen remembers them. */
export function toParams(criteria: Criteria): URLSearchParams {
  const params = new URLSearchParams();
  if (criteria.outcodes.length > 0) {
    params.set('outcodes', criteria.outcodes.join(','));
  }
  if (criteria.minBand) {
    params.set('minBand', criteria.minBand);
  }
  if (criteria.phase) {
    params.set('phase', criteria.phase);
    params.set('schoolMetres', String(criteria.schoolMetres ?? 800));
  }
  if (criteria.goodOnly) {
    params.set('goodOnly', 'true');
  }
  if (criteria.stationMetres) {
    params.set('stationMetres', String(criteria.stationMetres));
  }
  if (criteria.busMetres) {
    params.set('busMetres', String(criteria.busMetres));
  }
  return params;
}

/**
 * A distance from the query string, or null.
 *
 * <p>Not {@code Number(params.get(...))}: that turns a missing parameter into 0, which is a
 * number, and 0 metres is a criterion nothing can satisfy rather than the absence of one.
 */
function metres(params: URLSearchParams, key: string): number | null {
  const raw = params.get(key);
  return raw === null ? null : num(Number(raw));
}

/** And back again, so a link somebody was sent opens the same search. */
export function fromParams(params: URLSearchParams): Criteria {
  const outcodes = (params.get('outcodes') ?? '')
    .split(',')
    .map((code) => code.trim().toUpperCase())
    .filter(Boolean);
  const phase = params.get('phase');
  return {
    outcodes,
    minBand: params.get('minBand'),
    phase,
    schoolMetres: phase ? metres(params, 'schoolMetres') ?? 800 : null,
    goodOnly: params.get('goodOnly') === 'true',
    stationMetres: metres(params, 'stationMetres'),
    busMetres: metres(params, 'busMetres'),
  };
}
